import React, { useState, useEffect } from 'react';
import { PasslyCheckbox, PasslyTable } from '@app/components';
import { basicService, vaultsServiceNew } from '@app/services';
import { replaceDynamicTranslation } from '@app/helpers';
import { Order, OrderType, ITableHead, IDotMenu } from '@app/models';
import { PasslyTooltip } from '@app/componentsV2';
import { confirm } from '@app/components/Modal/confirmModal';
import { useTypedTranslation } from '@app/hooks';
import { useDispatch } from 'react-redux';
import { handleSetSnackbar } from '@app/store/actions/snackbar.actions';
import { TableCell } from '@material-ui/core';
import { IVaultGroup } from '@app/pages/passwordManager/interfaces';
import { sortObjectsByDisplayName } from '@app/pages/passwordManager/helpers';
import {AxiosResponse} from 'axios';
import SelectGroups from '@app/pages/passwordManager/components/dialogs/selectIdentities.component';

interface IProps {
    vaultId: string;
    groups: IVaultGroup[];
    showAddGroup: boolean;
    showAddGroupsModal: (show: boolean) => void;
}

interface IVaultGroupRow extends IVaultGroup {
    checked: boolean;
    [propName: string]: any;
}

enum TableHeadEnum {
    displayName = 'displayName',
    add = 'add',
    update = 'update',
    delete = 'delete',
    read = 'read',
    launch = 'launch'
}

const VaultAccess: React.SFC<IProps> = ({ vaultId, groups, showAddGroup, showAddGroupsModal }) => {
    const dispatch = useDispatch();

    const [rows, setRows] = useState<IVaultGroupRow[]>([]);
    const [groupsCount, setGroupsCount] = useState(0);
    const [order, setOrder] = useState<OrderType>(Order.asc);
    const [orderBy, setOrderBy] = useState('displayName');
    const [loading, setLoading] = useState(false);
    const { translation } = useTypedTranslation();

    useEffect(() => {
        const newRows = groups.map((group) => {
            return { ...group, checked: false, id: group.groupId };
        });

        setRows(newRows);
    }, [groups]);

    const prepareRowsToSend = (rows: IVaultGroupRow[]) => {
        return rows.map(row => {
            return {
                groupId: row.id,
                launch: row.launch,
                read: row.read,
                add: row.add,
                update: row.update,
                delete: row.delete,
            };
        });
    };

    const handleAddGroups = (ids: string[]) => {
        let jsonObject: { groups: IVaultGroup[] } = { groups: [] };

        jsonObject.groups = prepareRowsToSend(rows);

        ids.forEach((id) => {
            jsonObject.groups.push({
                groupId: id,
                launch: false,
                read: true,
                add: false,
                update: false,
                delete: false
            });
        });

        setLoading(false);
        vaultsServiceNew.updateVaultGroups(jsonObject, vaultId, handleGenericApiFailure, handleAddGroupSuccess);
    };

    const prepareResultData = (result: AxiosResponse) => {
        return result.data?.map((group: IVaultGroup) => {
            return { ...group, checked: false, id: group.groupId };
        });
    };

    const handleAddGroupSuccess = (result: AxiosResponse) => {
        setLoading(false);
        setRows(prepareResultData(result));

        showAddGroupsModal(false);
    };

    const onPermissionCheckboxChange = (index: number, name: string) => (checked: boolean) => {
        let updatedRow = rows[index];
        let jsonObject: { groups: IVaultGroup[] } = { groups: [] };

        updatedRow[name] = checked;

        jsonObject.groups = prepareRowsToSend(rows);

        const changedObject = {
            groupId: updatedRow.id,
            read: updatedRow.read,
            add: updatedRow.add,
            update: updatedRow.update,
            delete: updatedRow.delete,
            launch: updatedRow.launch
        };

        if (name === 'add') {
            if (checked) {
                changedObject.add = checked;
                changedObject.update = checked;
                changedObject.read = checked;
                changedObject.launch = checked;
            } else {
                changedObject.add = checked;
                changedObject.update = checked;
            }
        }

        if (name === 'update') {
            if (checked) {
                changedObject.update = checked;
                changedObject.read = checked;
                changedObject.launch = checked;
            } else {
                changedObject.update = checked;
            }
        }

        jsonObject.groups[index] = { ...jsonObject.groups[index], ...changedObject };
        setLoading(true);
        vaultsServiceNew.updateVaultGroups(jsonObject, vaultId, handleGenericApiFailure, handlePermissionChangeSuccess);
    };

    const handlePermissionChangeSuccess = (result: AxiosResponse) => {
        setLoading(false);
        const newR = prepareResultData(result);
        const sorted = sortObjectsByDisplayName(newR, order);

        setRows(sorted);
    };

    const handleGenericApiFailure = () => {
        setLoading(false);
        dispatch(handleSetSnackbar({ show: true, message: translation('GENERIC_ERROR'), severity: 'error', dismissOnly: true }));
        showAddGroupsModal(false);
    };

    const goToGroup = (row: IVaultGroupRow) => () => {
        basicService.redirect(`/directory/groups/details/${row.id}`, true);
    };

    const removeGroupsDialog = (ids: string[]) => {
        const singular: boolean = ids.length === 1;

        confirm(
            translation(singular ? 'VAULT_REMOVE_GROUP_BODY' : 'VAULT_REMOVE_GROUPS_BODY'),
            translation('REMOVE'),
            translation('CANCEL'),
            translation(singular ? 'VAULT_REMOVE_GROUP_HEADER' : 'VAULT_REMOVE_GROUPS_HEADER'),
            false,
            { isDeleteAction: true }
        ).then(
            () => {
                return removeGroups(ids);
            },
            () => {
                // do nothing on cancel
            }
        );
    };

    const removeGroups = (ids: string[]) => {
        let filteredRows = rows.filter(row => !ids.includes(row.id));
        let jsonObject: { groups: IVaultGroup[] } = { groups: [] };

        jsonObject.groups = prepareRowsToSend(filteredRows);

        vaultsServiceNew.updateVaultGroups(jsonObject, vaultId, handleGenericApiFailure, handleRemoveGroupsSuccess(ids));
    };

    const deleteRow = (e: any) => {
        let ids: string[] = [];
        ids.push(e.currentTarget.dataset.id);

        removeGroupsDialog(ids);
    };

    const deleteSelected = () => {
        let ids: string[] = [];

        rows.forEach((row) => {
            if (row.checked) {
                ids.push(row.id);
            }
        });

        removeGroupsDialog(ids);
    };

    const handleRemoveGroupsSuccess = (ids: string[]) => (result: AxiosResponse) => {
        setLoading(false);

        const newR = prepareResultData(result);
        const sorted = sortObjectsByDisplayName(newR, order);

        setGroupsCount(sorted?.length);
        setRows(sorted);

        const snackbarMessage = `${
            ids.length === 1
                ? replaceDynamicTranslation(translation('GENERIC_REMOVE_SUCCESS_SINGLE'), translation('GROUP'))
                : ids.length.toString() + ' ' + replaceDynamicTranslation(translation('GENERIC_REMOVE_SUCCESS_PLURAL'), translation('GROUPS'))
        }`.toLocaleLowerCase();

        dispatch(handleSetSnackbar({ show: true, message: snackbarMessage, severity: 'success' }));
    };

    const getPermissionCell = (group: IVaultGroupRow, index: number, name: string) => {
        const isAddCheckbox = name === 'add';
        const isDeleteCheckbox = name === 'delete';
        const isUpdateCheckbox = name === 'update';
        const isAddChecked = group.add && !isAddCheckbox && !isDeleteCheckbox;
        const isUpdateChecked = group.update && !isAddCheckbox && !isUpdateCheckbox && !isDeleteCheckbox;
        const isReadOrLaunchDisabled = (name === 'launch' && !group.read) || (name === 'read' && !group.launch);

        const isDisabled = isAddChecked || isUpdateChecked || isReadOrLaunchDisabled || group.IsAdmin;

        return (
            <>
                <PasslyTooltip title={isReadOrLaunchDisabled ? translation('VAULT_PERMISSIONS_READ_LAUNCH_TOOLTIP') : ''}>
                    <TableCell align='left' size='small'>
                        <PasslyCheckbox
                            name='select'
                            checked={group[name]}
                            onChange={onPermissionCheckboxChange(index, name)}
                            label=''
                            disabled={isDisabled}
                        />
                    </TableCell>
                </PasslyTooltip>
            </>
        );
    };

    const createSortHandler = () => () => {
        const ascDesc: OrderType = order === Order.asc ? Order.desc : Order.asc;

        setOrder(ascDesc);
        setRows(sortObjectsByDisplayName(rows, ascDesc));
    };

    const actionMenuItems: IDotMenu[] = [
        {
            name: translation('DELETE_SELECTED'),
            action: deleteSelected,
            isDeleteAction: true
        }
    ];

    const headCells: ITableHead[] = [
        {
            id: TableHeadEnum.displayName,
            label: 'GROUPS_NAME'
        },
        {
            id: TableHeadEnum.add,
            headerTooltipText: 'ADD_PERMISSION_TOOLTIP',
            label: 'VAULT_ADD',
            type: 'customCheckbox',
            hideSorting: true,
            getCustomCheckboxCell: getPermissionCell
        },
        {
            id: TableHeadEnum.update,
            headerTooltipText: 'UPDATE_PERMISSION_TOOLTIP',
            label: 'VAULT_UPDATE',
            type: 'customCheckbox',
            hideSorting: true,
            getCustomCheckboxCell: getPermissionCell
        },
        {
            id: TableHeadEnum.delete,
            headerTooltipText: 'DELETE_PERMISSION_TOOLTIP',
            label: 'VAULT_DELETE',
            type: 'customCheckbox',
            hideSorting: true,
            getCustomCheckboxCell: getPermissionCell
        },
        {
            id: TableHeadEnum.read,
            headerTooltipText: 'READ_PERMISSION_TOOLTIP',
            label: 'VAULT_READ',
            type: 'customCheckbox',
            hideSorting: true,
            getCustomCheckboxCell: getPermissionCell
        },
        {
            id: TableHeadEnum.launch,
            headerTooltipText: 'LAUNCH_PERMISSION_TOOLTIP',
            label: 'VAULT_LAUNCH',
            type: 'customCheckbox',
            hideSorting: true,
            getCustomCheckboxCell: getPermissionCell
        }
    ];

    return (
        <>
            {showAddGroup && (
                <SelectGroups vaultId={vaultId} groupsCount={rows.length} handleAddGroups={handleAddGroups} open={showAddGroup} closeCallback={showAddGroupsModal} />
            )}

            <PasslyTable
                rows={rows}
                setRows={setRows}
                order={order}
                orderBy={orderBy}
                handleOrderChange={createSortHandler}
                headerItems={headCells}
                rowsCount={groupsCount}
                onFetch={() => {}}
                withCheckboxes={true}
                fixedHeader={true}
                emptyListHelper={translation('VAULT_EMPTY_GROUP_LIST')}
                loadingData={loading}
                openInNewTab={goToGroup}
                deleteRow={deleteRow}
                actionMenuItems={actionMenuItems}
            />
        </>
    );
};

export default VaultAccess;
